/** Thin API wrapper for the SecuSign endpoints. */
import { ApiPaths, api } from "@/lib/api";
import type { SignatureTransaction } from "@/lib/types";


export interface SignerInput {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  language?: string;
  authentication_type?: "sms" | "email";
  automation?: boolean;
  order?: number;
  /** Optional links to a platform-known party so the audit log says
   *  "contact Y / user X signed" rather than just an email. */
  contact_id?: string | null;
  user_id?: number | null;
}


/** A session participant with their best-known signer identity, for the
 *  "send to a participant" picker. `missing` lists which required fields
 *  (first/last/email/phone) are still blank. */
export interface SignableParticipant {
  participant_id: string;
  role: string;
  label: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  contact_id: string | null;
  user_id: number | null;
  missing: string[];
}


export async function fetchSignableParticipants(
  sessionId: string,
): Promise<SignableParticipant[]> {
  const res = await api.get<SignableParticipant[]>(
    `/v1/sessions/${sessionId}/signable-participants`,
  );
  return res.data;
}


export async function kickoffSignature(
  evidenceId: string,
  payload: { signers: SignerInput[]; language?: string },
): Promise<SignatureTransaction> {
  const res = await api.post<SignatureTransaction>(
    ApiPaths.evidenceSign(evidenceId),
    payload,
  );
  return res.data;
}


export async function fetchSignature(
  signatureId: string,
): Promise<SignatureTransaction> {
  const res = await api.get<SignatureTransaction>(
    ApiPaths.signature(signatureId),
  );
  return res.data;
}


export async function fetchSignedPdfUrl(
  signatureId: string,
): Promise<string> {
  // In prod the endpoint returns a 302 to a presigned URL — let the
  // browser follow it by opening the endpoint directly. In dev/test
  // the backend returns {url: "mock-s3://..."}. Both flows handled
  // by callers via window.open(ApiPaths.signatureSignedPdf(id)).
  return ApiPaths.signatureSignedPdf(signatureId);
}
